import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'shared/helpers/dayjs';
import { Arrow, Communication } from 'icons/untitledui';
import {
  buildComposeUrl,
  EMAIL_PROVIDERS,
  PROVIDER_LABEL_KEYS,
} from 'shared/components/EmailComposeMenu/_index';
import {
  collectVisibleEntityTags,
  formatCategoryLabel,
} from 'shiftNotes/helpers/entityTagUtils';
import { TopBarMenuItem } from 'topBar/components/TopBar';
import { EventDetail } from 'events/_types';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import getNoteAuthorName from 'shiftNotes/helpers/getNoteAuthorName';
import getNoteTime from 'shiftNotes/helpers/getNoteTime';

export default function useEventShareMenuItems(
  event: EventDetail | undefined
): TopBarMenuItem[] {
  const { t } = useTranslation('pageEvents');
  const { t: tShared } = useTranslation('shared');
  const eventsCapture = usePosthogCapture();

  return useMemo(() => {
    if (!event) return [];

    const sortedNotes = [...event.shift_notes].sort(
      (a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf()
    );

    const notesText = sortedNotes
      .map((note) => {
        const date = dayjs(getNoteTime(note)).format('MMM D, YYYY, HH:mm');
        const header = `${getNoteAuthorName(note, t)} · ${date}`;
        const noteUrgency = note.entity_extractions?.find(
          (e) => e.extraction_class_key.toLowerCase() === 'urgency'
        )?.attributes?.categories?.[0];
        const urgencyLine = noteUrgency
          ? t('eventPage.emailUrgency', { value: noteUrgency })
          : '';
        const tags = collectVisibleEntityTags(note.entity_extractions ?? [])
          .map((tag) => formatCategoryLabel(tag.category))
          .join(', ');
        const tagsLine = tags ? t('eventPage.emailTags', { value: tags }) : '';
        return [header, note.edited_text, urgencyLine, tagsLine]
          .filter(Boolean)
          .join('\n');
      })
      .join('\n\n---\n\n');
    const emailBody = `${event.summary}\n\n${t(
      'eventPage.emailNotes'
    )}:\n\n${notesText}`;

    const shareSubItems: TopBarMenuItem[] = EMAIL_PROVIDERS.map((provider) => ({
      label: tShared(PROVIDER_LABEL_KEYS[provider]),
      icon: <Arrow.ArrowUpRight size={20} />,
      onClick: () => {
        window.open(
          buildComposeUrl(provider, { subject: event.title, body: emailBody }),
          '_blank',
          'noopener,noreferrer'
        );
        eventsCapture.eventEmailSent({ provider, event_id: event.id });
      },
    }));

    return [
      {
        label: t('eventPage.shareEmail'),
        onClick: () => {},
        icon: <Communication.Send01 size={20} />,
        subItems: shareSubItems,
        subHeaderLabel: tShared('emailCompose.shareWith'),
      },
    ];
  }, [event, t, tShared, eventsCapture]);
}
